import { api } from './api';
import { ramStand } from './ramvorrat';
import { useEditor } from './store';

/**
 * **Ein Neuladen, das niemand wollte, wird bemerkt** (Sascha, 16. Oktober:
 * „beim Spulen und Durchsliden hüpft der Player manchmal einfach komplett an
 * den Anfang (er lädt die Seite einfach neu)“ -- „Es kommt mir so vor, als
 * gäbe es einen Refresh, der das macht“).
 *
 * Zwei Wege führen am Telefon zu einem Neuladen, das niemand wollte: das
 * Herunterziehen am oberen Rand (Pull-to-Refresh), und der Browser, der einen
 * Tab mit viel Video aus dem Speicher wirft und ihn dann neu lädt. Gegen das
 * erste steht `overscroll-behavior` am Wurzelelement; nach dem zweiten
 * stellt der Schnittplatz den Kopf zurück, wo er stand (7.259).
 *
 * Welcher der beiden Wege es war, erfährt der Server: der Tab merkt sich
 * laufend, wo der Kopf steht. Beim ordentlichen Weggehen -- auch beim
 * Neuladen durch Herunterziehen -- kommt `pagehide`, und der Stand heisst
 * „sauber“. Fehlt das, hat der Browser die Seite abgeschossen.
 */

const SCHLUESSEL = 'schnitt-stand';

interface Stand {
  slug: string;
  kopf: number;
  /** Wann zuletzt geschrieben (ms seit 1970). */
  zeit: number;
  /** Ging die Seite ordentlich (`pagehide`)? */
  sauber: boolean;
  spielte: boolean;
  ram: ReturnType<typeof ramStand>;
}

function lies(): Stand | null {
  try {
    const roh = sessionStorage.getItem(SCHLUESSEL);
    return roh ? (JSON.parse(roh) as Stand) : null;
  } catch {
    return null;
  }
}

function schreib(stand: Stand): void {
  try {
    sessionStorage.setItem(SCHLUESSEL, JSON.stringify(stand));
  } catch {
    // Ohne Speicher fängt ein Neuladen eben vorn an -- wie bisher.
  }
}

export interface Neustart {
  kopf: number;
  /** `absturz`: kein `pagehide` -- der Browser hat die Seite abgeschossen. */
  art: 'absturz' | 'neuladen';
}

/**
 * Wurde dieses Projekt in diesem Tab gerade neu geladen? Ein frisches Öffnen
 * (über einen Link, aus der Liste) ist keines: dann fängt es vorn an.
 */
export function neustartPruefen(slug: string): Neustart | null {
  const stand = lies();
  if (!stand || stand.slug !== slug) return null;
  const art = (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type;
  if (!stand.sauber) return { kopf: stand.kopf, art: 'absturz' };
  if (art === 'reload') return { kopf: stand.kopf, art: 'neuladen' };
  return null;
}

/** Den Neustart melden -- mit dem, was der Tab zuletzt über sich wusste. */
export function neustartMelden(projectId: string, slug: string, neustart: Neustart): void {
  const stand = lies();
  const daten = {
    anlass: 'neustart',
    art: neustart.art,
    kopf: neustart.kopf,
    spielte: stand?.slug === slug ? stand.spielte : null,
    // Wie lange vor dem Ende der letzte Stand geschrieben wurde: bei einem
    // Absturz die Zeit, in der die Seite noch lebte.
    sekundenSeitStand: stand?.slug === slug ? Math.round((Date.now() - stand.zeit) / 100) / 10 : null,
    ramVorher: stand?.slug === slug ? stand.ram : null,
    fenster: { breite: window.innerWidth, hoehe: window.innerHeight },
  };
  void api.messwerte({ projectId, ua: navigator.userAgent, daten }).catch(() => undefined);
}

/**
 * Den Stand laufend merken, solange das Projekt offen ist. Gibt die Funktion
 * zum Aufhören zurück.
 */
export function standMerken(slug: string): () => void {
  let zuletzt = 0;
  const jetzt = (sauber: boolean) => {
    const s = useEditor.getState();
    schreib({ slug, kopf: s.playhead, zeit: Date.now(), sauber, spielte: s.playing, ram: ramStand() });
  };
  jetzt(false);
  // Höchstens alle halbe Sekunde: beim Spulen ändert sich der Kopf in jedem Bild.
  const ab = useEditor.subscribe((s, vorher) => {
    if (s.playhead === vorher.playhead && s.playing === vorher.playing) return;
    const t = performance.now();
    if (t - zuletzt < 500) return;
    zuletzt = t;
    jetzt(false);
  });
  const geht = () => jetzt(true);
  // Aus dem Zurück-Speicher wieder da: die Seite lebt wieder.
  const kommt = () => jetzt(false);
  window.addEventListener('pagehide', geht);
  window.addEventListener('pageshow', kommt);
  return () => {
    ab();
    window.removeEventListener('pagehide', geht);
    window.removeEventListener('pageshow', kommt);
    // Das Projekt wird verlassen (zurück zur Liste): kein Neuladen mehr.
    try {
      sessionStorage.removeItem(SCHLUESSEL);
    } catch {
      /* egal */
    }
  };
}
